import React, { useState, useCallback, useRef } from 'react';
import { Text } from '@mantine/core';
import CustomHoverCard from 'app/views/components/popover/custom-hover-card';
import { useSelector } from 'react-redux';
import { getFacilities } from 'app/state/ducks/facilities/selectors';
import { positionsForLender } from 'app/state/ducks/lenders-positions/thunks';
import { LendersPosition } from 'app/models/lenders-position-params';

interface SellerPositionHoverCardProps {
    sellerId: string;
    facilityIds?: string[];
    children: React.ReactNode;
}

const SellerPositionHoverCard: React.FC<SellerPositionHoverCardProps> = ({
    sellerId,
    facilityIds = [],
    children
}) => {
    const [positions, setPositions] = useState<LendersPosition[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const allFacilities = useSelector(getFacilities) || [];

    // The request only fetches the trade's facilities, so what was loaded is
    // only valid for that seller and that facility list. A mounted cell can
    // receive a new list (the grid updates rows in place), so the cache is
    // keyed by the request: a different key loads again, and a response from
    // an older request is dropped rather than shown as the current one.
    const requestKey = `${sellerId}|${[...facilityIds].sort().join(',')}`;
    // what the cell shows NOW, for a response that lands after the props moved
    const currentKey = useRef(requestKey);
    currentKey.current = requestKey;
    const loadedKey = useRef<string | null>(null);
    const inFlightKey = useRef<string | null>(null);

    const loadPositions = useCallback(async () => {
        if (!sellerId) return;
        if (loadedKey.current === requestKey || inFlightKey.current === requestKey) return;

        inFlightKey.current = requestKey;
        // the positions below are cleared, so what was loaded is gone: a
        // return to that key while this request is pending must load again
        loadedKey.current = null;
        setLoading(true);
        setHasLoaded(false);
        setPositions([]);
        try {
            // only the trade's facilities: the card shows nothing else
            const sellerPositions = await positionsForLender(sellerId, facilityIds);
            // superseded by a newer request, or the cell moved to another
            // seller / list without a new hover: either way not what it shows
            if (inFlightKey.current !== requestKey || currentKey.current !== requestKey) return;
            setPositions(sellerPositions || []);
            setHasLoaded(true);
            loadedKey.current = requestKey;
        } catch (error) {
            if (inFlightKey.current !== requestKey) return;
            console.error('Failed to load seller positions:', error);
            setPositions([]);
        } finally {
            if (inFlightKey.current === requestKey) {
                inFlightKey.current = null;
                setLoading(false);
            }
        }
    }, [sellerId, facilityIds, requestKey]);

    const filteredPositions = facilityIds.length > 0
        ? positions.filter(position =>
            position.contractId?.id && facilityIds.includes(position.contractId.id)
        )
        : positions;

    // loaded positions belong to the key they were loaded for, never another
    const showsCurrent = hasLoaded && loadedKey.current === requestKey;

    const renderPositionContent = () => {
        if (loading) {
            return (
                <div className='sp-hover-card-content'>
                    <Text className='sp-cover-card-field-value'>Loading position data...</Text>
                </div>
            );
        }

        if (!showsCurrent || filteredPositions.length === 0) {
            return (
                <div className='sp-hover-card-content'>
                    <Text className='sp-cover-card-field-value'>Seller currently has no position</Text>
                </div>
            );
        }

        const idsToRender = facilityIds.length > 0 ? facilityIds : filteredPositions.map(p => p.contractId?.id).filter(Boolean) as string[];

        return (
            <div className='sp-hover-card-content'>
                {idsToRender.map((facId, index) => {
                    const position = positions.find(p => p.contractId?.id === facId);
                    // The position carries its facility's name, type and currency
                    // (the positions read joins the facility); the facilities
                    // store is only a fallback, for a facility with no position.
                    const stored = allFacilities.find(f => f.id === facId);
                    const facility = {
                        name: position?.facilityName ?? stored?.name,
                        facilityType: position?.facilityType ?? stored?.facilityType,
                        baseCurrency: position?.baseCurrency ?? stored?.baseCurrency,
                    };

                    if (!position) {
                        return (
                            <div key={`no-pos-${facId}`} className='sp-hover-card-facility-group'>
                                <div className='sp-hover-card-field-group'>
                                    <Text className='sp-cover-card-field-heading'>Facility:</Text>
                                    <Text className='sp-cover-card-field-value'>{facility?.name || 'Unknown'}</Text>
                                </div>
                                <div className='sp-hover-card-field-group'>
                                    <Text className='sp-cover-card-field-value'>No position</Text>
                                </div>
                                {index < idsToRender.length - 1 && <div style={{ height: 8 }} />}
                            </div>
                        );
                    }

                    const lastProRata = position.proRatas?.[position.proRatas.length - 1];
                    const sdPosition = lastProRata?.amount || '0';

                    return (
                        <div key={`${position.id}-${index}`} className='sp-hover-card-facility-group'>
                            <div className='sp-hover-card-field-group'>
                                <Text className='sp-cover-card-field-heading'>Facility:</Text>
                                <Text className='sp-cover-card-field-value'>{facility?.name || 'Unknown'}</Text>
                            </div>
                            <div className='sp-hover-card-field-group'>
                                <Text className='sp-cover-card-field-heading'>Current Amount:</Text>
                                <Text className='sp-cover-card-field-value'>
                                    {parseFloat(sdPosition).toLocaleString('en-US', { minimumFractionDigits: 2 })} {facility?.baseCurrency || ''}
                                </Text>
                            </div>
                            <div className='sp-hover-card-field-group'>
                                <Text className='sp-cover-card-field-heading'>Facility Type:</Text>
                                <Text className='sp-cover-card-field-value'>{facility?.facilityType || 'Unknown'}</Text>
                            </div>
                            {index < idsToRender.length - 1 && <div style={{ height: 8 }} />}
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div onMouseEnter={loadPositions}>
            <CustomHoverCard
                targetComponent={children}
                cardTitle="Seller's Position"
                width='400px'
                maxHeight='400px'
            >
                {renderPositionContent()}
            </CustomHoverCard>
        </div>
    );
};

export default SellerPositionHoverCard;